/**
 * page-briefs: the action-first briefs of the operations pages that have no
 * composer in lib/ (Admin queue, Scan errors, Policy validation, Ad-hoc
 * scans, Tenants, Settings). Each returns an ActionBrief that the page hands
 * to BriefHero, so the page never assembles copy inline.
 *
 * Voice (CLAUDE.md Product Voice as amended by the Carbon blueprint §6): the
 * headline leads with the action, one lede names the subject and its count
 * in one bold run, the all-clear is "Nothing to review.", and every figure
 * comes from the caller's data. Nothing here invents a count: a page passes
 * known numbers only (it renders its loading or error brief otherwise).
 *
 * The lede never claims an order the input can't back: "has gone longest
 * without a scan" is the earliest real assessment (`lastAssessedDate`), "has
 * N of them" is a count.
 */
import type { ActionBrief } from '@/lib/brief-shape'
import { lastAssessmentAt } from '@/lib/tenant-assessed'

const DAY_MS = 24 * 60 * 60 * 1000
/** A scan job running longer than this is stuck (the queue's "Clear stuck jobs" rule). */
export const STUCK_AFTER_MS = 60 * 60 * 1000

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/* -------------------------------------------------------------------------- */
/* Admin queue                                                                */
/* -------------------------------------------------------------------------- */

export interface QueueBriefInput {
  queued: number
  running: number
  /** Running jobs past STUCK_AFTER_MS. */
  stuck: number
  healthy: boolean
}

export function composeQueueBrief({ queued, running, stuck, healthy }: QueueBriefInput): ActionBrief {
  const waiting = queued === 0 ? 'nothing is waiting' : `${queued} ${plural(queued, 'is', 'are')} waiting`
  if (!healthy) {
    return {
      headline: 'Check the scan processor before you queue more scans.',
      lede: `**The scan processor is not healthy**, and ${queued === 0 ? 'no job is waiting' : `${queued} ${plural(queued, 'job is', 'jobs are')} waiting`}.`,
    }
  }
  if (stuck > 0) {
    return {
      headline: stuck === 1 ? 'Clear the stuck job.' : `Clear the ${stuck} stuck jobs.`,
      lede: `**${stuck} running ${plural(stuck, 'job has', 'jobs have')} run for over an hour**, and ${waiting}.`,
    }
  }
  if (running === 0 && queued === 0) {
    return { headline: 'Nothing to review.', lede: '**The queue is empty**, and no scan is running.' }
  }
  if (running === 0) {
    return {
      headline: 'Leave the queue to run.',
      lede: `**${queued} ${plural(queued, 'job is', 'jobs are')} waiting**, and the processor is healthy.`,
    }
  }
  return {
    headline: 'Leave the queue to run.',
    lede:
      queued === 0
        ? `**${running} ${plural(running, 'job is', 'jobs are')} running**, none for over an hour, and nothing is waiting.`
        : `**${running} ${plural(running, 'job is', 'jobs are')} running and ${queued} ${plural(queued, 'is', 'are')} waiting**, with none running for over an hour.`,
  }
}

/* -------------------------------------------------------------------------- */
/* Scan errors                                                                */
/* -------------------------------------------------------------------------- */

export interface ScanErrorsBriefInput {
  totalErrors: number
  hoursBack: number
  tenantsAffected: number
  /** The tenant with the most failed or partial jobs (the caller sorts by count). */
  top?: { name: string; count: number }
}

export function composeScanErrorsBrief({ totalErrors, hoursBack, tenantsAffected, top }: ScanErrorsBriefInput): ActionBrief {
  if (totalErrors === 0 || !top) {
    return {
      headline: 'Nothing to review.',
      lede: `**No scan failed or finished partway in the last ${hoursBack} hours**.`,
    }
  }
  const head = `**${totalErrors} ${plural(totalErrors, 'scan', 'scans')} failed or finished partway in the last ${hoursBack} hours**`
  return {
    headline: `Start with ${top.name} today.`,
    emphasis: { text: top.name },
    lede:
      tenantsAffected <= 1
        ? `${head}, all on ${top.name}.`
        : `${head}, across ${tenantsAffected} tenants, and ${top.name} has ${top.count} of them.`,
  }
}

/* -------------------------------------------------------------------------- */
/* Policy validation                                                          */
/* -------------------------------------------------------------------------- */

export interface QaBriefInput {
  total: number
  tested: number
  fail: number
  investigate: number
}

export function composeQaBrief({ total, tested, fail, investigate }: QaBriefInput): ActionBrief {
  if (total === 0) {
    return { headline: 'Nothing to review.', lede: '**No policy is on the checklist yet**.' }
  }
  const head = `**${tested} of ${total} ${plural(total, 'policy is', 'policies are')} tested**`
  if (fail > 0) {
    return {
      headline: fail === 1 ? 'Start with the failing policy.' : `Start with the ${fail} failing policies.`,
      lede: `${head}, with ${fail} failing${investigate > 0 ? ` and ${investigate} to investigate` : ''}.`,
    }
  }
  if (investigate > 0) {
    return {
      headline: investigate === 1 ? 'Look into the flagged policy.' : `Look into the ${investigate} flagged policies.`,
      lede: `${head}, with ${investigate} marked to investigate.`,
    }
  }
  const untested = Math.max(0, total - tested)
  if (untested > 0) {
    return {
      headline: untested === 1 ? 'Test the untested policy.' : `Test the ${untested} untested policies.`,
      lede: `${head}, and none has failed.`,
    }
  }
  return { headline: 'Nothing to review.', lede: total === 1 ? '**The one policy passes**.' : `**All ${total} policies pass**.` }
}

/* -------------------------------------------------------------------------- */
/* Scan freshness (Tenants and Ad-hoc scans)                                  */
/* -------------------------------------------------------------------------- */

export interface ScanCandidate {
  name: string
  lastScanAt?: string | null
  /** The row's creation stamp: the backend sets `lastScanAt` to it on create, so a match is no scan. */
  createdAt?: string | null
}

/**
 * Parses a raw `lastScanAt` string, or null when it is absent, unparseable,
 * or the .NET DateTime.MinValue sentinel. A value without a zone is UTC (the
 * API omits the Z). It can't tell a creation stamp from a scan: read a
 * tenant through `lastAssessedDate` instead.
 */
export function lastScanDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`)
  if (Number.isNaN(d.getTime()) || d.getUTCFullYear() < 2000) return null
  return d
}

/**
 * When Guardian last assessed the tenant, or null when it never has: no
 * stamp, the MinValue sentinel, or the creation stamp the backend writes into
 * `lastScanAt` (`lastAssessmentAt`, lib/tenant-assessed.ts).
 */
export function lastAssessedDate(tenant: ScanCandidate): Date | null {
  return lastScanDate(lastAssessmentAt(tenant))
}

export interface ScanLead<T extends ScanCandidate> {
  brief: ActionBrief
  /** The tenant the headline names, when it names one. */
  lead: T | null
}

/**
 * The scan brief over the tenants Guardian can scan (the caller passes the
 * active ones). Never-scanned tenants come first, then any not scanned in the
 * last 24 hours, the one scanned longest ago leading.
 */
export function composeScanLeadBrief<T extends ScanCandidate>(
  candidates: T[],
  now: Date = new Date(),
  /** Tenants left out of `candidates` because they are inactive. */
  inactiveCount = 0,
): ScanLead<T> {
  const n = candidates.length
  if (n === 0 && inactiveCount > 0) {
    return {
      brief: {
        headline: 'Reactivate a tenant to resume scans.',
        lede:
          inactiveCount === 1
            ? '**The one tenant is inactive**, so Guardian scans nothing.'
            : `**All ${inactiveCount} tenants are inactive**, so Guardian scans none of them.`,
      },
      lead: null,
    }
  }
  if (n === 0) {
    return {
      brief: { headline: 'Add a tenant to begin monitoring.', lede: '**No tenant is monitored yet**, so Guardian has nothing to scan.' },
      lead: null,
    }
  }
  const never = candidates.filter((t) => lastAssessedDate(t) === null)
  if (never.length > 0) {
    const lead = never[0]
    return {
      brief:
        never.length === 1
          ? {
              headline: `Run the first scan on ${lead.name}.`,
              emphasis: { text: lead.name },
              lede: `**${lead.name} has never been scanned**, so Guardian has nothing to assess there yet.`,
            }
          : {
              headline: `Run the first scans on the ${never.length} unscanned tenants.`,
              lede: `**${never.length} of ${n} tenants have never been scanned**, so Guardian has nothing to assess there yet.`,
            },
      lead,
    }
  }
  const stale = candidates
    .map((t) => ({ t, at: lastAssessedDate(t) as Date }))
    .filter(({ at }) => now.getTime() - at.getTime() > DAY_MS)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
  if (stale.length > 0) {
    const lead = stale[0].t
    return {
      brief: {
        headline: stale.length === 1 ? `Scan ${lead.name} today.` : `Scan the ${stale.length} tenants not scanned in the last day.`,
        emphasis: stale.length === 1 ? { text: lead.name } : undefined,
        lede:
          stale.length === 1
            ? `**${lead.name} has not been scanned in the last 24 hours**.`
            : `**${stale.length} of ${n} tenants have not been scanned in the last 24 hours**, and ${lead.name} has gone longest without one.`,
      },
      lead,
    }
  }
  return {
    brief: {
      headline: 'Nothing to review.',
      lede: n === 1 ? `**${candidates[0].name} was scanned in the last 24 hours**.` : `**All ${n} tenants were scanned in the last 24 hours**.`,
    },
    lead: null,
  }
}

/* -------------------------------------------------------------------------- */
/* Settings                                                                   */
/* -------------------------------------------------------------------------- */

export interface SettingsTenantState {
  tenantId: string
  name: string
  /** The saved drift-email preference (not an unsaved edit). */
  driftEmailOn: boolean
  /** A multi-tenant app whose admin consent is missing or expired. */
  consentMissing: boolean
}

export interface SettingsLead {
  brief: ActionBrief
  /** The tenant the headline names, when it names one. */
  lead: SettingsTenantState | null
  /** What the lead needs: consent first, then drift email. */
  need: 'consent' | 'drift-email' | null
}

export function composeSettingsBrief(tenants: SettingsTenantState[]): SettingsLead {
  const n = tenants.length
  if (n === 0) {
    return {
      brief: {
        headline: 'Add a tenant to set up its notifications.',
        lede: '**No tenant is monitored yet**, so there is nothing to notify you about.',
      },
      lead: null,
      need: null,
    }
  }
  const noConsent = tenants.filter((t) => t.consentMissing)
  if (noConsent.length > 0) {
    const lead = noConsent[0]
    return {
      brief:
        noConsent.length === 1
          ? {
              headline: `Grant consent again for ${lead.name}.`,
              emphasis: { text: lead.name },
              lede: `**Admin consent for ${lead.name} is missing or expired**, and Guardian needs it to read the tenant.`,
            }
          : {
              headline: `Grant consent again for the ${noConsent.length} tenants.`,
              lede: `**Admin consent is missing or expired on ${noConsent.length} tenants**, and Guardian needs it to read them.`,
            },
      lead,
      need: 'consent',
    }
  }
  const off = tenants.filter((t) => !t.driftEmailOn)
  const on = n - off.length
  if (off.length > 0) {
    const lead = off[0]
    return {
      brief: {
        headline: off.length === 1 ? `Turn on drift email for ${lead.name}.` : `Turn on drift email for the ${off.length} tenants without it.`,
        emphasis: off.length === 1 ? { text: lead.name } : undefined,
        lede:
          n === 1
            ? `**Drift email is off for ${lead.name}**.`
            : on === 0
              ? `**Drift email is off for all ${n} tenants**.`
              : `**Drift email is on for ${on} of ${n} tenants**.`,
      },
      lead,
      need: 'drift-email',
    }
  }
  return {
    brief: {
      headline: 'Nothing to review.',
      lede: n === 1 ? `**Drift email is on for ${tenants[0].name}**.` : `**Drift email is on for all ${n} tenants**.`,
    },
    lead: null,
    need: null,
  }
}
